import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ProductTour } from '@/components/marketing/product-tour';
import { demoImage } from '@/components/marketing/product-tours';
import { Button } from '@/components/ui/button';
import { languageNames, supportedLanguages } from '@/i18n/languages';
import { cn } from '@/lib/utils';
import { type SharedData } from '@/types';
import { Head, Link, usePage } from '@inertiajs/react';
import { ArrowRight, ArrowUpRight, ChevronDown, Menu, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../../../css/marketing.css';

type MarketingSeo = {
    title: string;
    description: string;
    canonical: string;
    image: string;
    alternates: Record<string, string>;
    defaultUrl: string;
};
type MarketingPageProps = {
    auth: { user: { authenticated: true } | null };
    locale: SharedData['locale'];
    marketingSeo: MarketingSeo;
};
const founderLinkedIn = 'https://www.linkedin.com/in/sp4wnx/';

function Brand() {
    return (
        <Link href={route('home')} className="marketing-brand inline-flex min-h-11 shrink-0 items-center gap-2.5 rounded-md" aria-label="SmartKondo">
            <img
                src="/brand/smartkondo-supplied-horizontal.svg"
                width="187"
                height="44"
                alt="SmartKondo"
                className="h-auto w-36 rounded-md sm:w-[187px]"
            />
        </Link>
    );
}

function LocaleLinks() {
    const { t, i18n } = useTranslation('marketing');
    const { locale } = usePage<MarketingPageProps>().props;
    return (
        <nav
            aria-label={t('navigation.language')}
            className="inline-flex items-center gap-0.5 rounded-full border border-border p-1 text-xs font-semibold"
        >
            {supportedLanguages.map((language) => (
                <a
                    key={language}
                    href={locale.urls[language]}
                    lang={language}
                    hrefLang={language}
                    aria-label={languageNames[language]}
                    aria-current={i18n.resolvedLanguage === language ? 'true' : undefined}
                    className={cn(
                        'flex min-h-11 min-w-11 items-center justify-center rounded-full transition-colors hover:bg-muted',
                        i18n.resolvedLanguage === language && 'bg-foreground text-background hover:bg-foreground',
                    )}
                >
                    {language.toUpperCase()}
                </a>
            ))}
        </nav>
    );
}

export default function MarketingIndex() {
    const { auth, marketingSeo } = usePage<MarketingPageProps>().props;
    const { t } = useTranslation(['marketing', 'product-tour']);
    const [menuOpen, setMenuOpen] = useState(false);
    const menuButton = useRef<HTMLButtonElement>(null);
    const accessHref = route(auth.user ? 'dashboard' : 'register');
    const accessLabel = t(auth.user ? 'navigation.dashboard' : 'cta.access');
    const sections = ['how-it-works', 'projects', 'why', 'faq'] as const;

    const accessButton = (className?: string) => (
        <Button asChild className={cn('h-auto min-h-12 rounded-lg px-5 py-3 text-center text-sm font-semibold whitespace-normal', className)}>
            <Link href={accessHref}>
                {accessLabel}
                <ArrowUpRight className="size-4" aria-hidden="true" />
            </Link>
        </Button>
    );

    return (
        <>
            <Head title={marketingSeo.title}>
                <meta head-key="description" name="description" content={marketingSeo.description} />
                <link head-key="canonical" rel="canonical" href={marketingSeo.canonical} />
                {Object.entries(marketingSeo.alternates).map(([locale, href]) => (
                    <link key={locale} head-key={`alternate-${locale}`} rel="alternate" hrefLang={locale} href={href} />
                ))}
                <link head-key="alternate-default" rel="alternate" hrefLang="x-default" href={marketingSeo.defaultUrl} />
                <meta head-key="og:title" property="og:title" content={marketingSeo.title} />
                <meta head-key="og:description" property="og:description" content={marketingSeo.description} />
                <meta head-key="og:url" property="og:url" content={marketingSeo.canonical} />
                <meta head-key="og:image" property="og:image" content={marketingSeo.image} />
                <meta head-key="og:type" property="og:type" content="website" />
                <meta head-key="twitter:card" name="twitter:card" content="summary_large_image" />
            </Head>
            <div className="marketing min-h-svh bg-background text-foreground">
                <a href="#main-content" className="marketing-skip">
                    {t('navigation.skip')}
                </a>
                <header
                    className="relative z-20 border-b border-border bg-background"
                    onKeyDown={(event) => {
                        if (event.key === 'Escape' && menuOpen) {
                            setMenuOpen(false);
                            menuButton.current?.focus();
                        }
                    }}
                >
                    <div className="marketing-container flex min-h-20 items-center justify-between gap-4">
                        <Brand />
                        <nav aria-label={t('navigation.main')} className="hidden items-center gap-5 xl:flex">
                            {sections.map((section) => (
                                <a
                                    key={section}
                                    href={`#${section}`}
                                    className="py-3 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
                                >
                                    {t(`navigation.${section}`)}
                                </a>
                            ))}
                        </nav>
                        <div className="hidden items-center gap-4 xl:flex">
                            <LocaleLinks />
                            {!auth.user && (
                                <Link href={route('login')} className="inline-flex min-h-11 items-center text-sm font-semibold">
                                    {t('navigation.login')}
                                </Link>
                            )}
                            {accessButton()}
                        </div>
                        <div className="ml-auto flex items-center gap-2 xl:hidden">
                            <LanguageSwitcher className="min-h-11" />
                            <Button
                                ref={menuButton}
                                variant="ghost"
                                className="size-11"
                                aria-expanded={menuOpen}
                                aria-controls={menuOpen ? 'marketing-mobile-menu' : undefined}
                                aria-label={t(menuOpen ? 'navigation.close' : 'navigation.open')}
                                onClick={() => setMenuOpen(!menuOpen)}
                            >
                                {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
                            </Button>
                        </div>
                    </div>
                    {menuOpen && (
                        <nav
                            id="marketing-mobile-menu"
                            aria-label={t('navigation.main')}
                            className="marketing-container border-t border-border py-5 xl:hidden"
                            onKeyDown={(event) => {
                                if (event.key === 'Escape') {
                                    setMenuOpen(false);
                                    menuButton.current?.focus();
                                }
                            }}
                        >
                            <div className="grid gap-2">
                                {sections.map((section) => (
                                    <a
                                        key={section}
                                        href={`#${section}`}
                                        onClick={() => setMenuOpen(false)}
                                        className="rounded-md px-2 py-3 font-medium"
                                    >
                                        {t(`navigation.${section}`)}
                                    </a>
                                ))}
                                <div className="flex flex-wrap items-center justify-between gap-4 border-t border-border pt-4">
                                    <LocaleLinks />
                                    {!auth.user && (
                                        <Link href={route('login')} className="py-3 text-sm font-semibold">
                                            {t('navigation.login')}
                                        </Link>
                                    )}
                                </div>
                                {accessButton('mt-3 w-full')}
                            </div>
                        </nav>
                    )}
                </header>
                <main id="main-content" tabIndex={-1}>
                    <section className="marketing-hero relative overflow-hidden">
                        <div className="marketing-container relative grid items-center gap-12 py-14 sm:py-20 lg:grid-cols-[0.9fr_1.1fr] lg:py-24">
                            <div className="min-w-0">
                                <p className="marketing-eyebrow">{t('product-tour:hero.eyebrow')}</p>
                                <h1 className="mt-5 max-w-xl text-[clamp(2.65rem,4.8vw,4.6rem)] leading-[1.04] font-semibold tracking-[-0.055em] text-balance">
                                    {t('product-tour:hero.heading')} <span className="text-primary">{t('product-tour:hero.emphasis')}</span>
                                </h1>
                                <p className="mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground">{t('product-tour:hero.description')}</p>
                                <div className="mt-8 flex flex-wrap items-center gap-3">
                                    <Button asChild className="min-h-13 rounded-lg px-6 font-semibold">
                                        <a href="#how-it-works">
                                            {t('product-tour:hero.primary')}
                                            <ArrowRight className="size-4" aria-hidden="true" />
                                        </a>
                                    </Button>
                                    <Link href={accessHref} className="inline-flex min-h-13 items-center gap-2 px-3 text-sm font-semibold">
                                        {accessLabel}
                                        <ArrowUpRight className="size-4" aria-hidden="true" />
                                    </Link>
                                </div>
                            </div>
                            <figure className="marketing-hero-product relative min-w-0 lg:pl-2">
                                <a
                                    href="#demo-billing"
                                    className="marketing-hero-screen block overflow-hidden rounded-xl border border-border bg-card"
                                >
                                    <div className="flex items-center gap-1.5 border-b border-border px-4 py-3" aria-hidden="true">
                                        <span className="size-2 rounded-full bg-border" />
                                        <span className="size-2 rounded-full bg-border" />
                                        <span className="size-2 rounded-full bg-border" />
                                        <span className="ml-3 text-xs font-medium text-muted-foreground">SmartKondo</span>
                                    </div>
                                    <img
                                        src={demoImage('billing-en')}
                                        alt={t('product-tour:features.billing.alt')}
                                        width={1440}
                                        height={1000}
                                        fetchPriority="high"
                                        className="h-auto w-full"
                                    />
                                </a>
                                <figcaption className="mt-4 text-center text-xs leading-5 text-muted-foreground">
                                    {t('product-tour:sampleData')} · {t('product-tour:screenshotLanguage')}
                                </figcaption>
                            </figure>
                        </div>
                    </section>
                    <ProductTour />
                    <section id="projects" aria-labelledby="project-heading" className="border-y border-border">
                        <div className="marketing-container grid items-center gap-8 py-14 sm:py-16 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
                            <div>
                                <p className="marketing-eyebrow">{t('project.eyebrow')}</p>
                                <h2 id="project-heading" className="marketing-section-title mt-4">
                                    {t('project.heading')}
                                </h2>
                                <p className="mt-4 leading-7 text-muted-foreground">{t('project.description')}</p>
                            </div>
                            <div className="rounded-xl border border-border bg-muted/50 p-6 sm:p-8">
                                <p className="text-xs font-medium text-muted-foreground">{t('project.example')}</p>
                                <ol className="mt-5 grid gap-4 sm:grid-cols-3 sm:gap-5">
                                    {[0, 1, 2].map((index) => (
                                        <li key={index} className="relative min-w-0 border-t-2 border-primary/25 pt-4">
                                            <p className="text-lg font-semibold tracking-tight">{t(`project.steps.${index}.title`)}</p>
                                            <p className="mt-2 text-sm leading-6 text-muted-foreground">{t(`project.steps.${index}.description`)}</p>
                                            {index < 2 && (
                                                <ArrowRight className="absolute -top-2.5 right-0 size-5 bg-muted text-primary" aria-hidden="true" />
                                            )}
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        </div>
                    </section>
                    <section id="why" aria-labelledby="founder-heading" className="marketing-story">
                        <div className="marketing-container grid gap-10 py-16 sm:py-20 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20">
                            <div>
                                <p className="marketing-eyebrow">{t('founder.eyebrow')}</p>
                                <h2 id="founder-heading" className="marketing-section-title mt-4">
                                    {t('founder.heading')}
                                </h2>
                                <p className="mt-5 max-w-xl leading-7 text-muted-foreground">{t('founder.intro')}</p>
                                <a
                                    href={founderLinkedIn}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-4 inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-primary"
                                >
                                    {t('founder.linkedin')}
                                    <ArrowUpRight className="size-4" aria-hidden="true" />
                                </a>
                            </div>
                            <div className="rounded-xl border border-border bg-card p-6 sm:p-8">
                                <h3 className="text-xl font-semibold tracking-tight">{t('start.heading')}</h3>
                                <ol className="mt-6 space-y-5">
                                    {[0, 1, 2].map((index) => (
                                        <li key={index} className="flex items-start gap-3 text-sm leading-6">
                                            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary">
                                                {index + 1}
                                            </span>
                                            <span>{t(`start.steps.${index}`)}</span>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        </div>
                    </section>
                    <section id="faq" aria-labelledby="faq-heading" className="border-t border-border">
                        <div className="marketing-container grid gap-8 py-14 sm:py-16 lg:grid-cols-[0.8fr_1.2fr] lg:gap-24">
                            <h2 id="faq-heading" className="marketing-section-title">
                                {t('faq.heading')}
                            </h2>
                            <div>
                                {[0, 1, 2, 3].map((index) => (
                                    <details key={index} className="group border-b border-border first:border-t">
                                        <summary className="flex min-h-16 cursor-pointer list-none items-center justify-between gap-5 py-5 text-sm leading-6 font-semibold [&::-webkit-details-marker]:hidden">
                                            {t(`faq.items.${index}.question`)}
                                            <ChevronDown
                                                className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180 motion-reduce:transition-none"
                                                aria-hidden="true"
                                            />
                                        </summary>
                                        <p className="pr-6 pb-6 text-sm leading-7 text-muted-foreground">{t(`faq.items.${index}.answer`)}</p>
                                    </details>
                                ))}
                            </div>
                        </div>
                    </section>
                    <section className="marketing-closing text-white" aria-labelledby="closing-heading">
                        <div className="marketing-container flex flex-col items-start justify-between gap-8 py-16 sm:py-20 lg:flex-row lg:items-center">
                            <div className="max-w-2xl">
                                <h2 id="closing-heading" className="text-3xl leading-tight font-semibold tracking-tight sm:text-4xl">
                                    {t('product-tour:final.heading')}
                                </h2>
                            </div>
                            <div className="shrink-0">
                                {accessButton('min-h-13 bg-white px-7 text-slate-950 hover:bg-white/90')}
                                <p className="mt-3 max-w-64 text-xs leading-5 text-white/70">{t(auth.user ? 'hero.signedIn' : 'closing.note')}</p>
                            </div>
                        </div>
                    </section>
                </main>
                <footer className="marketing-container py-10">
                    <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-center">
                        <div>
                            <Brand />
                            <p className="mt-2 text-sm text-muted-foreground">{t('footer.tagline')}</p>
                        </div>
                        <nav
                            aria-label={t('navigation.footer')}
                            className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground"
                        >
                            <a className="inline-flex items-center gap-1 py-3" href={founderLinkedIn} target="_blank" rel="noopener noreferrer">
                                {t('footer.linkedin')}
                                <ArrowUpRight className="size-3.5" aria-hidden="true" />
                            </a>
                            <Link className="py-3" href={route('legal.privacy')}>
                                {t('footer.privacy')}
                            </Link>
                            <Link className="py-3" href={route('legal.terms')}>
                                {t('footer.terms')}
                            </Link>
                            <Link className="py-3" href={route(auth.user ? 'dashboard' : 'login')}>
                                {t(auth.user ? 'navigation.dashboard' : 'navigation.login')}
                            </Link>
                        </nav>
                    </div>
                    <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-6">
                        <p className="text-xs text-muted-foreground">© SmartKondo. {t('footer.rights')}</p>
                        <LocaleLinks />
                    </div>
                </footer>
            </div>
        </>
    );
}
